import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { authApi } from '@/services/api';
import { ROUTES } from '@/routes';

/** Deletes the account and everything Tally stores about it, after the user types DELETE. */
export function DeleteAccountSection() {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      await authApi.deleteAccount();
      // A full reload drops every cached query and the signed-in state.
      window.location.assign(ROUTES.home);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the account');
      setDeleting(false);
    }
  };

  return (
    <section className="rounded-xl border border-destructive/40 bg-card p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-destructive">
        <AlertTriangle className="h-5 w-5" /> Delete account
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Permanently deletes your account and everything Tally stores about you: connected inboxes (Google access is
        revoked), downloaded emails, expenses, trips, tags, subscriptions, people and settings. This cannot be undone.
        Signing in again later starts an empty account.
      </p>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="mt-4 rounded-lg border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
        >
          Delete my account…
        </button>
      ) : (
        <div className="mt-4 space-y-3">
          <label htmlFor="confirm-delete" className="block text-sm">
            Type <span className="font-mono font-semibold">DELETE</span> to confirm
          </label>
          <input
            id="confirm-delete"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            className="w-full max-w-xs rounded-lg border border-input bg-background px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={remove}
              disabled={typed !== 'DELETE' || deleting}
              className="flex items-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />} Permanently delete everything
            </button>
            <button onClick={() => { setOpen(false); setTyped(''); }} disabled={deleting} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )}
    </section>
  );
}
